import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AppPrefsDTO, BehaviorPrefs, MeetingPrefs } from '../../global';

/**
 * Kişisel tercihler (özet dili ve davranış anahtarları) tek yerde tutuluyor:
 * hem Ayarlar ekranı hem de kayıt kancası (kayıt sesleri) aynı değeri okuyor.
 *
 * Tema burada değil, `lib/theme.tsx`'te: ilk çizimden önce gerekli olduğu için
 * kendi sağlayıcısı var. İkisi de aynı ayar dosyasına yazıyor, ana süreç
 * gelen yamaları birleştiriyor.
 */

/** Ana süreçten cevap gelene kadar kullanılan değerler (settings.ts ile aynı). */
const FALLBACK_BEHAVIOR: BehaviorPrefs = {
  launchAtLogin: false,
  recordingSounds: true,
  recordingShortcut: true,
  recordingNotesShortcut: true,
  quickFindShortcut: true,
  hideFromScreenShare: false,
};

const FALLBACK_MEETINGS: MeetingPrefs = {
  calendarReminders: true,
  detectCalls: true,
  autoStopOnCallEnd: true,
};

/** Ayarlar ekranının yazabildiği alanlar (ana sürecin ürettikleri hariç). */
export type PrefsPatch = Partial<
  Omit<AppPrefsDTO, 'failedShortcuts' | 'shortcuts' | 'callDetectionSupported'>
>;

const FALLBACK_PREFS: AppPrefsDTO = {
  summaryLanguage: 'auto',
  summaryStyle: { rules: [], templateId: 'general' },
  transcriptTerms: [],
  theme: 'system',
  behavior: FALLBACK_BEHAVIOR,
  meetings: FALLBACK_MEETINGS,
  callDetectionSupported: false,
  failedShortcuts: [],
  shortcuts: {
    'toggle-recording': 'Control+Alt+Shift+R',
    'recording-notes': 'Control+Alt+Shift+N',
    'quick-find': 'Control+Alt+Shift+K',
  },
};

interface PreferencesContextValue {
  prefs: AppPrefsDTO;
  /** Ana süreçten yanıt gelene kadar false — Ayarlar ekranı yükleniyor gösterir. */
  isLoaded: boolean;
  savePrefs: (patch: PrefsPatch) => Promise<void>;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<AppPrefsDTO>(FALLBACK_PREFS);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    // Promise.resolve: köprü hazır değilse senkron hata çizimi bozmasın.
    void Promise.resolve()
      .then(() => window.zabit.getAppPrefs())
      .then((next) => {
        if (!alive) return;
        setPrefs(next);
        setIsLoaded(true);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const savePrefs = useCallback(async (patch: PrefsPatch) => {
    const saved = await window.zabit.saveAppPrefs(patch);
    setPrefs(saved);
  }, []);

  const value = useMemo(() => ({ prefs, isLoaded, savePrefs }), [prefs, isLoaded, savePrefs]);
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const context = useContext(PreferencesContext);
  if (!context) throw new Error('usePreferences, PreferencesProvider dışında kullanıldı.');
  return context;
}
